import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import {
  ANTECEDENT_OPTIONS,
  BEHAVIOR_TYPE_OPTIONS,
  CONSEQUENCE_OPTIONS,
  FUNCTIONS_OF_BEHAVIOR,
  FUNCTION_BY_VALUE,
  LOCATION_OPTIONS,
} from '../../../lib/pbisConstants';

const CHART_OPTIONS = [
  { value: 'frequency', label: 'Daily Frequency' },
  { value: 'function', label: 'Function of Behavior' },
  { value: 'antecedent', label: 'Antecedent Breakdown' },
  { value: 'behavior-type', label: 'Behavior Type' },
  { value: 'time-of-day', label: 'Time of Day' },
  { value: 'location', label: 'By Location' },
  { value: 'intensity', label: 'Intensity Trend' },
];

const TIME_RANGE_OPTIONS = [
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
  { value: 'all', label: 'All Time' },
];

const LABEL_LOOKUPS = {
  antecedent: Object.fromEntries(ANTECEDENT_OPTIONS.map((o) => [o.value, o.label])),
  behavior: Object.fromEntries(BEHAVIOR_TYPE_OPTIONS.map((o) => [o.value, o.label])),
  consequence: Object.fromEntries(CONSEQUENCE_OPTIONS.map((o) => [o.value, o.label])),
  location: Object.fromEntries(LOCATION_OPTIONS.map((o) => [o.value, o.label])),
};

const humanizeEnum = (kind, value) => {
  if (!value) return '';
  return LABEL_LOOKUPS[kind]?.[value] || String(value).replace(/-/g, ' ');
};

const DATA_POINTS_SORT_OPTIONS = [
  { value: 'chronological', label: 'Oldest → Newest (see escalation)' },
  { value: 'reverse', label: 'Newest → Oldest' },
];

const FUNCTION_COLORS = Object.fromEntries(FUNCTIONS_OF_BEHAVIOR.map((f) => [f.value, f.color]));

const timeOf = (i) => new Date(i?.occurredAt || i?.timestamp || 0);

const BehaviorAnalytics = ({ incidents = [], selectedStudent }) => {
  const [timeRange, setTimeRange] = useState('30days');
  const [chartType, setChartType] = useState('function');

  // Chronological detail list beneath the chart. Same pattern the IEP
  // GoalDetailView uses ("Recent data points" list under the progress
  // chart) — it's the data behind the chart in reading order so escalation
  // is easy to see ("went from one hitting incident to three per day").
  // Defaults to oldest-first for that reason; a Newest-first toggle covers
  // the reviewer who wants the most-recent story on top.
  const [dataPointsSort, setDataPointsSort] = useState('chronological');
  const [dataPointsOnlyWithNotes, setDataPointsOnlyWithNotes] = useState(false);
  const [showAllDataPoints, setShowAllDataPoints] = useState(false);

  const filteredIncidents = useMemo(() => {
    if (!incidents?.length) return [];
    if (timeRange === 'all') return incidents;
    const days = parseInt(timeRange.replace('days', ''), 10);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return incidents.filter((incident) => timeOf(incident) >= cutoff);
  }, [incidents, timeRange]);

  const chartData = useMemo(() => {
    switch (chartType) {
      case 'frequency':
        return generateFrequencyData(filteredIncidents);
      case 'function':
        return generateFunctionData(filteredIncidents);
      case 'antecedent':
        return generateAntecedentData(filteredIncidents);
      case 'behavior-type':
        return generateBehaviorTypeData(filteredIncidents);
      case 'time-of-day':
        return generateTimeOfDayData(filteredIncidents);
      case 'location':
        return generateLocationData(filteredIncidents);
      case 'intensity':
        return generateIntensityData(filteredIncidents);
      default:
        return [];
    }
  }, [filteredIncidents, chartType]);

  function generateFrequencyData(rows) {
    const buckets = [];
    for (let i = 13; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const key = date.toISOString().split('T')[0];
      const dayIncidents = rows.filter((r) => timeOf(r).toISOString().split('T')[0] === key);
      buckets.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        incidents: dayIncidents.length,
      });
    }
    return buckets;
  }

  function generateFunctionData(rows) {
    const counts = {};
    rows.forEach((r) => {
      const f = r?.hypothesizedFunction || 'unknown';
      counts[f] = (counts[f] || 0) + 1;
    });
    return FUNCTIONS_OF_BEHAVIOR.filter((f) => counts[f.value]).map((f) => ({
      name: f.label,
      value: counts[f.value],
      color: f.color,
    }));
  }

  function generateAntecedentData(rows) {
    const counts = {};
    rows.forEach((r) => {
      const key = r?.antecedent;
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([value, count]) => ({
        antecedent: LABEL_LOOKUPS.antecedent[value] || value,
        count,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }

  function generateBehaviorTypeData(rows) {
    const counts = {};
    rows.forEach((r) => {
      const key = r?.behaviorType;
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([value, count]) => ({
        behavior: LABEL_LOOKUPS.behavior[value] || value,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }

  function generateTimeOfDayData(rows) {
    // Group hours into school-day buckets so patterns show up quickly.
    const buckets = [
      { key: 'Before 8AM', start: 0, end: 8, count: 0 },
      { key: '8-10AM', start: 8, end: 10, count: 0 },
      { key: '10AM-12PM', start: 10, end: 12, count: 0 },
      { key: '12-2PM', start: 12, end: 14, count: 0 },
      { key: '2-4PM', start: 14, end: 16, count: 0 },
      { key: 'After 4PM', start: 16, end: 24, count: 0 },
    ];
    rows.forEach((r) => {
      const hour = timeOf(r).getHours();
      const bucket = buckets.find((b) => hour >= b.start && hour < b.end);
      if (bucket) bucket.count += 1;
    });
    return buckets.map((b) => ({ time: b.key, count: b.count }));
  }

  function generateLocationData(rows) {
    const counts = {};
    rows.forEach((r) => {
      const key = r?.location;
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([value, count]) => ({
        location: LABEL_LOOKUPS.location[value] || value,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }

  function generateIntensityData(rows) {
    const buckets = [];
    for (let i = 13; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const key = date.toISOString().split('T')[0];
      const dayIncidents = rows.filter((r) => timeOf(r).toISOString().split('T')[0] === key);
      const avg =
        dayIncidents.length > 0
          ? Math.round(
              (dayIncidents.reduce((sum, r) => sum + (r?.intensity ?? 0), 0) / dayIncidents.length) *
                10
            ) / 10
          : 0;
      buckets.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        avgIntensity: avg,
      });
    }
    return buckets;
  }

  const insights = useMemo(() => {
    if (!filteredIncidents.length) return [];
    const out = [];

    // Dominant function
    const fnCounts = {};
    filteredIncidents.forEach((r) => {
      if (!r?.hypothesizedFunction) return;
      fnCounts[r.hypothesizedFunction] = (fnCounts[r.hypothesizedFunction] || 0) + 1;
    });
    const dominantFn = Object.entries(fnCounts).sort((a, b) => b[1] - a[1])[0];
    if (dominantFn && dominantFn[1] > 1) {
      const meta = FUNCTIONS_OF_BEHAVIOR.find((f) => f.value === dominantFn[0]);
      const pct = Math.round((dominantFn[1] / filteredIncidents.length) * 100);
      out.push({
        title: 'Dominant function of behavior',
        description: `${meta?.label || dominantFn[0]} accounts for ${pct}% of incidents — target this in the BSP.`,
        icon: 'Brain',
        color: 'text-primary',
      });
    }

    // Top antecedent
    const antCounts = {};
    filteredIncidents.forEach((r) => {
      if (!r?.antecedent) return;
      antCounts[r.antecedent] = (antCounts[r.antecedent] || 0) + 1;
    });
    const topAnt = Object.entries(antCounts).sort((a, b) => b[1] - a[1])[0];
    if (topAnt && topAnt[1] > 1) {
      out.push({
        title: 'Most common antecedent',
        description: `${LABEL_LOOKUPS.antecedent[topAnt[0]] || topAnt[0]} precedes ${Math.round(
          (topAnt[1] / filteredIncidents.length) * 100
        )}% of incidents.`,
        icon: 'AlertTriangle',
        color: 'text-warning',
      });
    }

    // Peak hour
    const hourCounts = {};
    filteredIncidents.forEach((r) => {
      const hour = timeOf(r).getHours();
      hourCounts[hour] = (hourCounts[hour] || 0) + 1;
    });
    const peakHour = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0];
    if (peakHour) {
      const h = parseInt(peakHour[0], 10);
      const label = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
      out.push({
        title: 'Peak time',
        description: `Most incidents occur around ${label}.`,
        icon: 'Clock',
        color: 'text-secondary',
      });
    }

    // Intensity trend
    if (filteredIncidents.length >= 6) {
      const sorted = [...filteredIncidents].sort((a, b) => timeOf(a) - timeOf(b));
      const half = Math.floor(sorted.length / 2);
      const first = sorted.slice(0, half);
      const last = sorted.slice(half);
      const firstAvg = first.reduce((s, r) => s + (r?.intensity ?? 0), 0) / first.length;
      const lastAvg = last.reduce((s, r) => s + (r?.intensity ?? 0), 0) / last.length;
      if (lastAvg < firstAvg - 0.5) {
        out.push({
          title: 'Improving trend',
          description: `Recent intensity (${lastAvg.toFixed(1)}) is lower than earlier (${firstAvg.toFixed(1)}). Keep supports in place.`,
          icon: 'TrendingDown',
          color: 'text-success',
        });
      } else if (lastAvg > firstAvg + 0.5) {
        out.push({
          title: 'Escalating pattern',
          description: `Recent intensity (${lastAvg.toFixed(1)}) exceeds earlier (${firstAvg.toFixed(1)}). Review supports.`,
          icon: 'TrendingUp',
          color: 'text-destructive',
        });
      }
    }

    return out;
  }, [filteredIncidents]);

  const renderChart = () => {
    if (chartData?.length === 0) {
      return (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <Icon name="BarChart3" size={48} className="mx-auto mb-4 opacity-50" />
            <p>Log a few incidents to see patterns emerge here.</p>
          </div>
        </div>
      );
    }

    const barPalette = [
      '#2563eb',
      '#7c3aed',
      '#d97706',
      '#dc2626',
      '#0891b2',
      '#16a34a',
      '#db2777',
      '#65a30d',
    ];

    switch (chartType) {
      case 'frequency':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
              <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
              <Tooltip />
              <Bar dataKey="incidents" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'function':
        return (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color || FUNCTION_COLORS[entry.name] || barPalette[index % barPalette.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'antecedent':
        return (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="antecedent" width={180} stroke="hsl(var(--muted-foreground))" />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {chartData.map((_, index) => (
                  <Cell key={index} fill={barPalette[index % barPalette.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case 'behavior-type':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="behavior" stroke="hsl(var(--muted-foreground))" />
              <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'time-of-day':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" />
              <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
              <Tooltip />
              <Bar dataKey="count" fill="#7c3aed" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'location':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="location" width={140} stroke="hsl(var(--muted-foreground))" />
              <Tooltip />
              <Bar dataKey="count" fill="#0891b2" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'intensity':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
              <YAxis domain={[0, 5]} stroke="hsl(var(--muted-foreground))" />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="avgIntensity"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ fill: 'hsl(var(--primary))' }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-card-foreground">Behavior Analytics</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Pattern analysis for {selectedStudent?.firstName
                ? `${selectedStudent.firstName} ${selectedStudent.lastName ?? ''}`.trim()
                : selectedStudent?.name || 'selected student'}
            </p>
          </div>
          <Button variant="outline" size="sm">
            <Icon name="Share" size={16} />
            Share Report
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Select label="Time Range" options={TIME_RANGE_OPTIONS} value={timeRange} onChange={setTimeRange} />
          <Select label="Chart Type" options={CHART_OPTIONS} value={chartType} onChange={setChartType} />
        </div>

        <div className="bg-muted/30 rounded-lg p-4">{renderChart()}</div>

        {/* Data points behind the chart --------------------------------
            Every incident that produced the chart above, listed in
            reading order so the reviewer can trace an escalation
            pattern (e.g. "went from one 'hit peer' incident to three
            per day, notes call out worsening"). This is the same
            'chart on top + individual data points below' layout the
            IEP GoalDetailView uses under 'Recent data points'. */}
        <DataPointsBehindChart
          incidents={filteredIncidents}
          sort={dataPointsSort}
          onSortChange={setDataPointsSort}
          onlyWithNotes={dataPointsOnlyWithNotes}
          onOnlyWithNotesChange={setDataPointsOnlyWithNotes}
          showAll={showAllDataPoints}
          onShowAllChange={setShowAllDataPoints}
        />
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Brain" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">Data-Driven Insights</h3>
        </div>

        {insights.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Lightbulb" size={48} className="text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">Log a few incidents to see insights.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {insights.map((insight, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
                <Icon name={insight.icon} size={20} className={insight.color} />
                <div>
                  <h4 className="font-medium text-card-foreground">{insight.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{insight.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon="Calendar"
          color="text-primary"
          label="Total Incidents"
          value={filteredIncidents.length}
        />
        <StatCard
          icon="TrendingUp"
          color="text-warning"
          label="Avg. Intensity"
          value={
            filteredIncidents.length > 0
              ? (
                  Math.round(
                    (filteredIncidents.reduce((s, r) => s + (r?.intensity ?? 0), 0) /
                      filteredIncidents.length) *
                      10
                  ) / 10
                ).toFixed(1)
              : '0.0'
          }
        />
        <StatCard
          icon="Clock"
          color="text-secondary"
          label="Avg. Duration"
          value={(() => {
            const withDuration = filteredIncidents.filter((r) => r?.durationSeconds != null);
            if (!withDuration.length) return '0m';
            const avg =
              withDuration.reduce((s, r) => s + (r.durationSeconds || 0), 0) / withDuration.length;
            return `${Math.round((avg / 60) * 10) / 10}m`;
          })()}
        />
        <StatCard
          icon="Flag"
          color="text-destructive"
          label="Follow-up"
          value={filteredIncidents.filter((r) => r?.followUpRequired).length}
        />
      </div>
    </div>
  );
};

const StatCard = ({ icon, color, label, value }) => (
  <div className="bg-card rounded-lg border border-border p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon name={icon} size={16} className={color} />
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
    </div>
    <p className="text-2xl font-bold text-card-foreground">{value}</p>
  </div>
);

// ---------------------------------------------------------------------------
// DataPointsBehindChart
//
// Individual behavior-incident entries beneath the chart above them. The
// goal is not to duplicate the Data Table (that lives on its own tab and
// is a wide sortable grid) — it's to let a reviewer read the chart and
// then scan the specific incidents that produced it in reading order.
//
// Layout mirrors the IEP GoalDetailView "Recent data points" list: one
// line per incident, date + what happened on top, teacher notes on
// the next line highlighted so "what was said and what was done" is the
// thing the eye lands on. When the current chart's timeRange has more
// than 8 incidents we cap the list at 8 by default and offer a "Show all
// N" toggle — the whole caseload's history is available on the Data
// Table tab.
// ---------------------------------------------------------------------------
const DataPointsBehindChart = ({
  incidents,
  sort,
  onSortChange,
  onlyWithNotes,
  onOnlyWithNotesChange,
  showAll,
  onShowAllChange,
}) => {
  const rows = useMemo(() => {
    const base = onlyWithNotes
      ? (incidents || []).filter(
          (i) =>
            (i?.notes && i.notes.trim().length > 0) ||
            (i?.antecedentNotes && i.antecedentNotes.trim().length > 0),
        )
      : incidents || [];
    return [...base].sort((a, b) => {
      const ta = new Date(a?.occurredAt || a?.timestamp || 0).getTime();
      const tb = new Date(b?.occurredAt || b?.timestamp || 0).getTime();
      return sort === 'reverse' ? tb - ta : ta - tb;
    });
  }, [incidents, sort, onlyWithNotes]);

  const totalCount = rows.length;
  const capped = totalCount > 8 && !showAll;
  const visibleRows = capped ? rows.slice(0, 8) : rows;
  const notesCount = rows.filter(
    (i) =>
      (i?.notes && i.notes.trim().length > 0) ||
      (i?.antecedentNotes && i.antecedentNotes.trim().length > 0),
  ).length;

  if (!incidents || incidents.length === 0) {
    return (
      <div className="mt-6 rounded-lg border border-dashed border-border p-6 text-center">
        <Icon
          name="ListChecks"
          size={40}
          className="text-muted-foreground mx-auto mb-3 opacity-50"
        />
        <p className="text-sm text-muted-foreground">
          Each incident you log will appear here, in order, with its notes —
          so you can see if the behavior escalated over time.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-card-foreground flex items-center gap-2">
            <Icon name="ListChecks" size={16} className="text-primary" />
            Data points behind this chart
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {totalCount} incident{totalCount === 1 ? '' : 's'}
            {notesCount > 0 && (
              <>
                {' '}·{' '}<span className="font-medium">{notesCount}</span> with notes
              </>
            )}
            {' '}· read top-to-bottom to see escalation over time.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-foreground select-none">
            <input
              type="checkbox"
              checked={onlyWithNotes}
              onChange={(e) => onOnlyWithNotesChange(Boolean(e.target.checked))}
              className="rounded border-border focus:ring-primary"
            />
            Only with notes
          </label>
          <div className="min-w-56">
            <Select
              options={DATA_POINTS_SORT_OPTIONS}
              value={sort}
              onChange={onSortChange}
            />
          </div>
        </div>
      </div>

      {totalCount === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center">
          <p className="text-sm text-muted-foreground">
            No incidents match the current filter.
          </p>
        </div>
      ) : (
        <ol className="relative border-l-2 border-border/60 space-y-3 pl-4">
          {visibleRows.map((incident, index) => (
            <DataPointRow
              key={incident?.id || index}
              incident={incident}
              orderNumber={
                sort === 'reverse' ? totalCount - index : index + 1
              }
            />
          ))}
        </ol>
      )}

      {capped && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onShowAllChange(true)}
          >
            <Icon name="ChevronDown" size={14} />
            Show all {totalCount} incidents
          </Button>
        </div>
      )}
      {showAll && totalCount > 8 && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onShowAllChange(false)}
          >
            <Icon name="ChevronUp" size={14} />
            Show fewer
          </Button>
        </div>
      )}
    </div>
  );
};

const intensityChipClasses = (intensity) => {
  if (!intensity) return 'bg-muted text-muted-foreground border border-border';
  const n = Number(intensity);
  if (n <= 2) return 'bg-success/10 text-success border border-success/30';
  if (n === 3) return 'bg-warning/10 text-warning border border-warning/30';
  return 'bg-destructive/10 text-destructive border border-destructive/30';
};

const intensityLabel = (intensity) => {
  if (!intensity) return '—';
  const n = Number(intensity);
  if (n <= 2) return `Low · ${n}/5`;
  if (n === 3) return 'Medium · 3/5';
  return `High · ${n}/5`;
};

const DataPointRow = ({ incident, orderNumber }) => {
  const at = new Date(incident?.occurredAt || incident?.timestamp || 0);
  const dateLabel = at.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeLabel = at.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const fn = incident?.hypothesizedFunction
    ? FUNCTION_BY_VALUE[incident.hypothesizedFunction]
    : null;
  const behaviorLabel = humanizeEnum('behavior', incident?.behaviorType);
  const antecedentLabel = humanizeEnum('antecedent', incident?.antecedent);
  const consequenceLabel = humanizeEnum('consequence', incident?.consequence);
  const locationLabel = humanizeEnum('location', incident?.location);
  const description = (incident?.behaviorDescription || '').trim();
  const notes = (incident?.notes || '').trim();
  const antecedentNotes = (incident?.antecedentNotes || '').trim();
  return (
    <li className="relative">
      {/* Left-rail marker dot lines up with the vertical timeline line. */}
      <span
        className="absolute -left-[22px] top-2 w-3 h-3 rounded-full border-2 border-background"
        style={{ backgroundColor: fn?.color || 'hsl(var(--muted-foreground))' }}
        aria-hidden="true"
      />
      <div className="rounded-lg border border-border bg-background p-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="text-xs font-mono text-muted-foreground">
            #{orderNumber}
          </span>
          <span className="font-medium text-card-foreground">{dateLabel}</span>
          <span className="text-xs text-muted-foreground">at {timeLabel}</span>
          {locationLabel && (
            <span className="text-xs text-muted-foreground">
              · {locationLabel}
            </span>
          )}
          <span
            className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${intensityChipClasses(
              incident?.intensity,
            )}`}
          >
            {intensityLabel(incident?.intensity)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <span className="text-sm font-semibold text-card-foreground">
            {behaviorLabel || 'Incident'}
          </span>
          {fn && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
              style={{
                borderColor: fn.color,
                color: fn.color,
                backgroundColor: `${fn.color}14`,
              }}
            >
              {fn.label}
            </span>
          )}
          {incident?.followUpRequired && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-warning/10 text-warning border border-warning/30">
              <Icon name="Flag" size={10} />
              Follow-up
            </span>
          )}
        </div>
        {(antecedentLabel || consequenceLabel) && (
          <p className="text-xs text-muted-foreground mt-1.5">
            {antecedentLabel && (
              <>
                <span className="font-semibold">A:</span> {antecedentLabel}
              </>
            )}
            {antecedentLabel && consequenceLabel && (
              <span className="mx-1.5 text-muted-foreground/60">→</span>
            )}
            {consequenceLabel && (
              <>
                <span className="font-semibold">C:</span> {consequenceLabel}
              </>
            )}
          </p>
        )}
        {description && (
          <p className="text-sm text-card-foreground mt-2 whitespace-pre-wrap">
            {description}
          </p>
        )}
        {(notes || antecedentNotes) && (
          <div className="mt-2 rounded-md border-l-4 border-primary/60 bg-primary/5 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-primary/80 mb-1">
              What was said &amp; done
            </p>
            {notes && (
              <p className="text-sm text-card-foreground whitespace-pre-wrap">
                {notes}
              </p>
            )}
            {antecedentNotes && (
              <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">
                <span className="font-medium">Antecedent detail: </span>
                {antecedentNotes}
              </p>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

export default BehaviorAnalytics;
